import {
  Avatar,
  Badge,
  Box,
  CollapseCard,
  FlexBox,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useMemo, useRef } from 'react';

import { ConversationCustomFieldsForm } from 'components/conversation/conversationCustomFieldsForm';
import {
  useCustomFields,
  useRequiredAndVisibleCustomFields,
} from 'utils/customFields';
import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';
import { useCurrentUserInAcw } from 'utils/user';

import { CardMoveHandle } from '../../components/cardMoveHandle/cardMoveHandle';

import styles from './conversationCustomFieldsCard.module.scss';

type ConversationCustomFieldsCardProps = Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove'
> & {
  csid: number;
  isMinimized: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

export const ConversationCustomFieldsCard = ({
  csid,
  isMinimized,
  isOpen,
  index,
  type,
  onMove,
  onToggle,
}: ConversationCustomFieldsCardProps): JSX.Element | null => {
  const row = useRef<HTMLDivElement>(null);
  const { customFields, rawResponse } = useCustomFields({
    entityId: 'conversation',
    entityInstanceId: `${csid}`,
  });

  const isCurrentUserInAcw = useCurrentUserInAcw({ csid });

  const missingRequiredAndVisibleAttributesCount =
    useRequiredAndVisibleCustomFields(customFields).length;

  const badge = useMemo(() => {
    if (!missingRequiredAndVisibleAttributesCount) {
      return null;
    }

    return (
      <Tooltip
        content={
          <FlexBox flexDirection="column" gap="100">
            <Box>
              {missingRequiredAndVisibleAttributesCount} required{' '}
              {missingRequiredAndVisibleAttributesCount > 1
                ? 'attributes (*) are'
                : 'attribute (*) is'}{' '}
              missing value.
            </Box>

            {isCurrentUserInAcw && (
              <Box>
                Wrap-up time will not end until all required attributes are
                filled in.
              </Box>
            )}
          </FlexBox>
        }
      >
        <Badge
          className={classnames({ [styles.badge]: !isMinimized })}
          variant="warning"
          size="small"
        >
          {missingRequiredAndVisibleAttributesCount} *
        </Badge>
      </Tooltip>
    );
  }, [
    isCurrentUserInAcw,
    isMinimized,
    missingRequiredAndVisibleAttributesCount,
  ]);

  const inputIcon = <Avatar icon="input" />;

  if (!customFields.length || !rawResponse.attributes?.isSuccess) {
    return null;
  }

  if (isMinimized) {
    return (
      <FlexBox flexDirection="column" gap="100">
        <div className="icon-holder">{inputIcon}</div>
        {badge}
      </FlexBox>
    );
  }

  return (
    <CollapseCard containerRef={row} isOpen={isOpen} onToggle={onToggle}>
      <CollapseCard.Header
        avatar={inputIcon}
        slotBeforeTitle={
          <CardMoveHandle
            index={index}
            target={row}
            type={type}
            onMove={onMove}
          />
        }
      >
        Conversation attributes
        {badge}
      </CollapseCard.Header>
      <CollapseCard.Content>
        <ConversationCustomFieldsForm customFields={customFields} csid={csid} />
      </CollapseCard.Content>
    </CollapseCard>
  );
};
